import React from 'react';
import { shallowEqual } from 'react-redux';
import { useMount } from 'ahooks';
import {
  IApiaComponent,
  TApiaComponent,
  TApiaFieldType,
  TFileUploaded,
} from '../../../types';
import IProperty from '../../../JSApi/constants/IProperty';
import Field from '../../../JSApi/constants/Field';
import { useAppSelector } from '../../../hooks/hooks';
import {
  FieldErrorMsg,
  FieldLabel,
  TooltipHelp,
  ApiaDebugInfo,
} from './common';
import { obj2Arr, getComposedID } from '../../../utils/util';
import ApiaComponentContext, {
  IApiaComponentContext,
} from './apiaComponent/ApiaComponentContext';
import { ApiaFormContext } from './page/ApiaForm';
import useContext from '../../../hooks/useContext';
import ApiaInput from './fields/ApiaInput';
import ApiaSelect from './fields/ApiaSelect';
import ApiaButton from './fields/ApiaButton';
import ApiaCheckbox from './fields/ApiaCheckbox';
import ApiaImage from './fields/ApiaImage';
import ApiaText from './fields/ApiaText';
import ApiaRadioButton from './fields/ApiaRadioButton';
import ApiaDatePicker from './fields/ApiaDatePicker';
import ApiaHiddenInput from './fields/ApiaHiddenInput';
import ApiaLink from './fields/ApiaLink';
import ApiaMultiple from './fields/ApiaMultiple';
import ApiaTextArea from './fields/ApiaTextArea';
import ApiaTitle from './fields/ApiaTitle';
import ApiaCaptcha from './fields/ApiaCaptcha';
import ApiaFinder from './fields/apiaFinder/ApiaFinder';
import ApiaUploader from './fields/apiaUploader/ApiaUploader';
import ApiaTree from './fields/apiaTree/ApiaTree';
import ApiaDataGrid from './fields/apiaDataGrid/ApiaDataGrid';
import { IApiaDataGrid } from '../../../store/dataGridSlice/types';
import propsStore from '../../../store/propsStore/propsStore';
import usePropsSelector from '../../../store/propsStore/usePropsSelector';
import ApiaEditor from './fields/ApiaEditor';
import { DataGridIdentifier } from './fields/apiaDataGrid/context';
import store from '../../../store';
import { GridTranslation } from './common/GridTranslation';

const renderComponent = (field: TApiaComponent): JSX.Element | null => {
  const { fieldType, valueType } = field;
  switch (fieldType) {
    case Field.TYPE_INPUT:
    case Field.TYPE_PASSWORD:
      if ('qryId' in field) {
        return <ApiaFinder />;
      }
      if (valueType === 'D') {
        return <ApiaDatePicker />;
      }
      return <ApiaInput />;
    case Field.TYPE_SELECT:
      return <ApiaSelect />;
    case Field.TYPE_BUTTON:
      return <ApiaButton />;
    case Field.TYPE_RADIO:
      return <ApiaRadioButton />;
    case Field.TYPE_CHECK:
      return <ApiaCheckbox />;
    case Field.TYPE_AREA:
      return <ApiaTextArea />;
    case Field.TYPE_MULTIPLE:
      return <ApiaMultiple />;
    case Field.TYPE_FILE:
      return <ApiaUploader />;
    case Field.TYPE_GRID:
      return <ApiaDataGrid />;
    case Field.TYPE_TITLE:
      return <ApiaTitle />;
    case Field.TYPE_LABEL:
      return <ApiaText />;
    case Field.TYPE_HREF:
      return <ApiaLink />;
    case Field.TYPE_IMAGE:
      return <ApiaImage />;
    case Field.TYPE_HIDDEN:
      return <ApiaHiddenInput />;
    case Field.TYPE_EDITOR:
      return <ApiaEditor />;
    case Field.TYPE_CAPTCHA:
      return <ApiaCaptcha />;
    case Field.TYPE_TREE:
      return <ApiaTree />;
    default:
      break;
  }
  return null;
};

export type TApiaDataGridProps = {
  startIndex: number;
  curPage: number;
  pages: number;
  rowCount: number;
};

const getGridProps = (
  fieldType: TApiaFieldType,
  fieldObj: TApiaComponent,
): TApiaDataGridProps | null => {
  if (fieldType === Field.TYPE_GRID) {
    const f = fieldObj as unknown as IApiaDataGrid;
    return {
      startIndex: parseInt(f.startIndex, 10),
      curPage: parseInt(f.curPage, 10),
      pages: parseInt(f.pages, 10),
      rowCount: parseInt(f.rowCount, 10),
    };
  }
  return null;
};

const getUploaderProps = (
  fieldType: TApiaFieldType,
  fieldObj: TApiaComponent,
): { fileProperties: TFileUploaded[] } | null => {
  if (fieldType === Field.TYPE_FILE) {
    const fileProps = fieldObj.file_prp;
    if (fileProps && fileProps?.length > 0) {
      const parsedFileProps = fileProps?.replace(
        /([{,])(\s*)([A-Za-z0-9_-]+?)\s*:/g,
        '$1"$3":',
      );
      const props = {
        fileProperties: obj2Arr(
          eval(`(${parsedFileProps})`),
        ) as TFileUploaded[],
      };
      return props;
    }
  }
  return null;
};

export function isTrue(value: any) {
  return (
    value?.toLowerCase?.() === 'true' ||
    value?.toLowerCase?.() === 't' ||
    value?.toLowerCase?.() === 'v' ||
    value === true ||
    value === 1
  );
}
export function isFalse(value: any) {
  return (
    value?.toLowerCase?.() === 'false' ||
    value?.toLowerCase?.() === 'f' ||
    value === false ||
    value === 0
  );
}

const ApiaComponent = ({
  colWidth,
  field,
  gridColumn,
  gridFieldId,
  gridId,
  index = 0,
  onGrid = false,
}: IApiaComponent): JSX.Element | null => {
  const {
    formId,
    isFormReadOnly,
    frmParent,
    parsedFormId,
    formData: { formName },
  } = useContext(ApiaFormContext);

  const { id, fieldType, attId, attName, attLabel, valueType, properties } =
    field;

  const fieldId = gridFieldId || getComposedID(formId, id, onGrid, index);
  const name = `${(properties?.name ? properties.name : fieldId).replaceAll(
    '.',
    '_',
  )}`;

  const validationEvent = useAppSelector(
    (state) => state.valuesSlice[fieldId]?.valueState?.event,
  );

  const gridIdentifier = React.useContext(DataGridIdentifier);
  const isGridReadonly = React.useMemo(() => {
    if (gridIdentifier?.gridId) {
      return store.getState().dataGridSlice[gridIdentifier.gridId].context.grid
        .field.properties.readonly;
    }
    return false;
  }, [gridIdentifier?.gridId]);

  useMount(() => {
    const currentProps = propsStore.getFieldProps(fieldId);
    const isReadonly =
      [
        String(properties.readonly),
        String(properties.readOnly),
        String(isFormReadOnly),
        String(isGridReadonly),
      ].findIndex((currentReadonly) => isTrue(currentReadonly)) !== -1;
    if (!currentProps?.formData)
      propsStore.updateField(fieldId, {
        attributes: {
          attId,
          attName,
          attLabel,
          colWidth,
          fieldType,
          index,
          onGrid,
          valueType,
          ...getUploaderProps(fieldType, field),
          ...getGridProps(fieldType, field),
        },
        field,
        formData: {
          id: formId,
          frmParent,
          frmId: parsedFormId,
          frmName: formName,
          readOnly: isFormReadOnly,
        },
        gridColumn,
        name,
        parentGridId: gridId,
        ...properties,
        isActuallyReadonly: isReadonly,
      });
  });

  const contextValue: IApiaComponentContext = React.useMemo(
    () => ({
      fieldId,
      field,
      name,
      onGrid,
    }),
    [field, fieldId, name, onGrid],
  );

  const { isActuallyReadonly, ...storeProps } = usePropsSelector(
    fieldId,
    (currentProps) => {
      const returnProps = {
        [IProperty.PROPERTY_TOOLTIP]: currentProps[IProperty.PROPERTY_TOOLTIP],
        [IProperty.PROPERTY_REQUIRED]: isTrue(
          currentProps[IProperty.PROPERTY_REQUIRED],
        ),
        [IProperty.PROPERTY_FONT_COLOR]:
          currentProps[IProperty.PROPERTY_FONT_COLOR],
        [IProperty.PROPERTY_TITLE]: currentProps[IProperty.PROPERTY_TITLE],
        [IProperty.PROPERTY_INPUT_AS_TEXT]: isTrue(
          currentProps[IProperty.PROPERTY_INPUT_AS_TEXT],
        ),
        [IProperty.PROPERTY_DISABLED]: isTrue(
          currentProps[IProperty.PROPERTY_DISABLED],
        ),
        [IProperty.PROPERTY_SHOW_TOOLTIP_AS_HELP]: isTrue(
          currentProps[IProperty.PROPERTY_SHOW_TOOLTIP_AS_HELP],
        ),
        formData: currentProps.formData,
        name: currentProps.name,
        message: currentProps.message,
        reqTrad: isTrue(currentProps.reqTrad),
        isActuallyReadonly:
          currentProps.isActuallyReadonly || currentProps?.noModify,
      };
      return returnProps;
    },
    shallowEqual,
  );

  if (storeProps?.formData) {
    return (
      <>
        <ApiaComponentContext value={contextValue}>
          {!onGrid &&
            fieldType !== Field.TYPE_BUTTON &&
            fieldType !== Field.TYPE_CHECK &&
            fieldType !== Field.TYPE_HIDDEN &&
            fieldType !== Field.TYPE_RADIO && (
              <FieldLabel
                htmlFor={
                  !['', Field.TYPE_RADIO, Field.TYPE_GRID].includes(
                    fieldType,
                  ) && !isActuallyReadonly
                    ? fieldId
                    : undefined
                }
                label={attLabel || properties.gridTitle}
                tooltip={
                  storeProps[IProperty.PROPERTY_TITLE] ||
                  storeProps[IProperty.PROPERTY_TOOLTIP]
                }
                required={
                  storeProps[IProperty.PROPERTY_REQUIRED] ||
                  fieldType === Field.TYPE_CAPTCHA ||
                  storeProps.reqTrad
                }
                fontColor={storeProps[IProperty.PROPERTY_FONT_COLOR]}
                isInputAsText={
                  storeProps[IProperty.PROPERTY_INPUT_AS_TEXT] || isFormReadOnly
                }
              />
            )}
          <ApiaDebugInfo id={fieldId} data={field} />
          {renderComponent(field)}
          <GridTranslation />
        </ApiaComponentContext>
        {!isFormReadOnly &&
          validationEvent !== '' &&
          !storeProps[IProperty.PROPERTY_INPUT_AS_TEXT] &&
          !storeProps[IProperty.PROPERTY_DISABLED] &&
          fieldType !== Field.TYPE_HIDDEN && (
            <>
              <FieldErrorMsg name={storeProps.name}>
                {storeProps.message}
              </FieldErrorMsg>
              <TooltipHelp
                show={
                  storeProps[IProperty.PROPERTY_SHOW_TOOLTIP_AS_HELP] || false
                }
                asText={storeProps[IProperty.PROPERTY_INPUT_AS_TEXT]}
              >
                {storeProps[IProperty.PROPERTY_TOOLTIP] || null}
              </TooltipHelp>
            </>
          )}
      </>
    );
  }
  return null;
};

export default React.memo(ApiaComponent);
